import { useEffect } from 'react';
import { socialRealtimeService } from '../services/socialService';

/**
 * Custom hook for Social Hub real-time subscriptions
 * Subscribes to duels, friendships, and squad updates
 */
export const useSocialRealtime = (userId, callbacks) => {
  const { onDuelUpdate, onFriendshipUpdate, onSquadUpdate } = callbacks || {};

  useEffect(() => {
    if (!userId) return;

    // Subscribe to duel updates
    const duelChannel = socialRealtimeService?.subscribeToDuelUpdates(userId, (payload) => {
      onDuelUpdate?.(payload);
    });

    // Subscribe to friendship updates
    const friendshipChannel = socialRealtimeService?.subscribeToFriendshipUpdates(userId, (payload) => {
      onFriendshipUpdate?.(payload);
    });

    // Subscribe to squad member updates
    const squadMemberChannel = socialRealtimeService?.subscribeToSquadMemberUpdates((payload) => {
      onSquadUpdate?.(payload);
    });

    // Subscribe to squad invitation updates
    const squadInvitationChannel = socialRealtimeService?.subscribeToSquadInvitationUpdates(userId, (payload) => {
      onSquadUpdate?.(payload);
    });

    // Cleanup subscriptions
    return () => {
      duelChannel?.unsubscribe();
      friendshipChannel?.unsubscribe();
      squadMemberChannel?.unsubscribe();
      squadInvitationChannel?.unsubscribe();
    };
  }, [userId, onDuelUpdate, onFriendshipUpdate, onSquadUpdate]);
};

/**
 * Custom hook for friend requests real-time updates
 */
export const useFriendRequestsRealtime = (userId, onUpdate) => {
  useEffect(() => {
    if (!userId) return;

    const channel = socialRealtimeService?.subscribeToFriendshipUpdates(userId, (payload) => {
      // Trigger update callback for any friendship change
      onUpdate?.(payload);
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [userId, onUpdate]);
};

/**
 * Custom hook for squad members real-time updates
 */
export const useSquadMembersRealtime = (squadId, onUpdate) => {
  useEffect(() => {
    if (!squadId) return;

    const channel = socialRealtimeService?.subscribeToSquadMemberUpdates((payload) => {
      // Filter for specific squad and trigger update
      if (payload?.new?.squad_id === squadId || payload?.old?.squad_id === squadId) {
        onUpdate?.(payload);
      }
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [squadId, onUpdate]);
};

/**
 * Custom hook for incoming duel invites real-time updates
 * Fires when a new pending duel is created where the user is the opponent
 */
export const useDuelInvitesRealtime = (userId, onInvite) => {
  useEffect(() => {
    if (!userId) return;

    const channel = socialRealtimeService?.subscribeToDuelInvites(userId, (payload) => {
      onInvite?.(payload);
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [userId, onInvite]);
};

/**
 * Custom hook for leaderboard real-time changes
 * Fires when any user_profile is updated (points, streak, duels_won)
 */
export const useLeaderboardRealtime = (onLeaderboardChange) => {
  useEffect(() => {
    const channel = socialRealtimeService?.subscribeToLeaderboardChanges((payload) => {
      onLeaderboardChange?.(payload);
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [onLeaderboardChange]);
};

/**
 * Custom hook for friend workout activity real-time updates
 * Fires when any new workout_log is inserted
 */
export const useFriendActivityRealtime = (userId, onActivity) => {
  useEffect(() => {
    if (!userId) return;

    const channel = socialRealtimeService?.subscribeToFriendWorkoutActivity((payload) => {
      onActivity?.(payload);
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [userId, onActivity]);
};
